import { Button, Collapse, Textarea, UnstyledButton } from '@mantine/core';
import {
    ArrowUp,
    CaretRight,
    Check,
    Copy,
    Stop,
} from '@phosphor-icons/react';
import {
    useReportingThread,
    type ReportMessage,
    type SubmitToolResponseArgs,
} from '@hooks/useReportingThread';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSearchParams } from 'react-router';

import styles from './ReportsContainer.module.css';
import type { TurnSavedReport } from './reportTurn';
import { ReportTurnProvider } from './ReportTurnContext';
import ToolCallRenderer from './ToolCallRenderer';
import { groupTurns, splitLead, type Turn } from './turns';

/** Questions that show what the agent is for; each one sends on click. */
const SUGGESTIONS = [
    'Which SKUs are at risk of stocking out in the next 14 days?',
    'Top 10 SKUs by inventory in internal warehouses',
    'Monthly sales by channel for the last 6 months as a chart',
    'Which purchase orders to vendors are still open?',
];

export default function ReportsContainer() {
    const {
        messages,
        isStreaming,
        error,
        sendMessage,
        stop,
        submitToolResponse,
        runSavedReport,
    } = useReportingThread();
    const turns = useMemo(() => groupTurns(messages), [messages]);
    // Reports saved (or updated) by a turn of this conversation, by turn id.
    const [savedByTurn, setSavedByTurn] = useState<Record<string, TurnSavedReport>>({});
    const onTurnSaved = useCallback((turnId: string, savedReport: TurnSavedReport) => {
        setSavedByTurn((prev) => (prev[turnId]?.id === savedReport.id && prev[turnId]?.name === savedReport.name ? prev : { ...prev, [turnId]: savedReport }));
    }, []);
    // The saved report each turn is editing: the latest one this conversation
    // opened or saved before it. A change asked for after opening a report is
    // saved back onto that report, not as a copy.
    const editingByTurn = useMemo(() => {
        const out: Record<string, TurnSavedReport | null> = {};
        let current: TurnSavedReport | null = null;
        for (const turn of turns) {
            if (turn.user?.savedReport) current = turn.user.savedReport;
            out[turn.id] = current;
            if (savedByTurn[turn.id]) current = savedByTurn[turn.id];
        }
        return out;
    }, [turns, savedByTurn]);
    const [input, setInput] = useState('');
    const threadEndRef = useRef<HTMLDivElement | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const autoRunFiredRef = useRef(false);

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    // Auto-run a saved report when the library page sends us here with
    // ?run=<id>&name=<label>. Fires once per query-param lifecycle —
    // the ref guard stops React's strict-mode double-invoke + StrictMode
    // re-renders from triggering twice.
    useEffect(() => {
        const runId = searchParams.get('run');
        const name = searchParams.get('name');
        if (!runId || autoRunFiredRef.current) return;
        autoRunFiredRef.current = true;
        void runSavedReport({
            savedReportId: runId,
            displayLabel: name ?? 'Saved report',
        });
        // Clear the query string so a manual refresh doesn't re-trigger.
        setSearchParams({}, { replace: true });
    }, [searchParams, runSavedReport, setSearchParams]);

    const onSubmit = async () => {
        const trimmed = input.trim();
        if (!trimmed || isStreaming) return;
        setInput('');
        await sendMessage(trimmed);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void onSubmit();
        }
    };

    const started = messages.length > 0;
    const composer = (
        <Composer
            value={input}
            onChange={setInput}
            onKeyDown={onKeyDown}
            onSubmit={() => void onSubmit()}
            onStop={stop}
            isStreaming={isStreaming}
            autoFocus={!started}
        />
    );

    return (
        <div className={styles.page}>
            {started ? (
                <>
                    <div className={styles.header}>
                        <h1 className={styles.headerTitle}>Reports</h1>
                    </div>
                    <div className={styles.thread}>
                        <div className={styles.threadInner}>
                            {turns.map((turn) => (
                                <TurnBlock
                                    key={turn.id}
                                    turn={turn}
                                    editingReport={editingByTurn[turn.id] ?? null}
                                    onSaved={onTurnSaved}
                                    onSubmitToolResponse={submitToolResponse}
                                    onStop={stop}
                                />
                            ))}
                            {error && <div className={styles.errorBanner}>{error}</div>}
                            <div ref={threadEndRef} />
                        </div>
                    </div>
                    <div className={styles.composerDock}>
                        <div className={styles.composerInner}>{composer}</div>
                    </div>
                </>
            ) : (
                /* Nothing asked yet: the question box sits in the middle of
                   the page, the way a blank chat does, and drops to the
                   bottom once the first answer is on its way. */
                <div className={styles.landing}>
                    <div className={styles.landingInner}>
                        <h1 className={styles.landingTitle}>{greeting()}</h1>
                        <p className={styles.landingSubtitle}>
                            Ask about stock, sales, orders, suppliers or stores. You get a table you can download, and the answer in a few lines.
                        </p>
                        {composer}
                        <div className={styles.suggestions}>
                            {SUGGESTIONS.map((question) => (
                                <button
                                    key={question}
                                    type="button"
                                    className={styles.suggestion}
                                    onClick={() => void sendMessage(question)}
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                        {error && <div className={styles.errorBanner}>{error}</div>}
                    </div>
                </div>
            )}
        </div>
    );
}


/** "Good morning, Priya" from the signed-in user and the clock; "Good morning" when the name is unknown. */
function greeting(): string {
    const hour = new Date().getHours();
    const time = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    try {
        const user = JSON.parse(localStorage.getItem('user') ?? '{}') as { firstName?: string };
        return user.firstName ? `${time}, ${user.firstName}` : time;
    } catch {
        return time;
    }
}

/**
 * The question box: a rounded card with the text inside and one round
 * button in the corner that sends, or stops while an answer is being
 * built. Used in the middle of a blank page and docked at the bottom
 * of a conversation, so both read as the same control.
 */
function Composer({
    value,
    onChange,
    onKeyDown,
    onSubmit,
    onStop,
    isStreaming,
    autoFocus,
}: {
    value: string;
    onChange: (value: string) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onSubmit: () => void;
    onStop: () => void;
    isStreaming: boolean;
    autoFocus?: boolean;
}) {
    return (
        <div className={styles.composer}>
            <Textarea
                className={styles.composerTextarea}
                classNames={{ input: styles.composerInput }}
                variant="unstyled"
                placeholder="Ask for a report, e.g. top 10 SKUs by units last month"
                value={value}
                onChange={(e) => onChange(e.currentTarget.value)}
                onKeyDown={onKeyDown}
                autosize
                minRows={2}
                maxRows={8}
                disabled={isStreaming}
                autoFocus={autoFocus}
                aria-label="Report question"
            />
            <div className={styles.composerBar}>
                <span className={styles.composerHint}>Enter to send · Shift+Enter for a new line</span>
                {isStreaming ? (
                    <Button
                        variant="light"
                        color="gray"
                        size="compact-sm"
                        onClick={onStop}
                        rightSection={<Stop size={12} weight="fill" />}
                    >
                        Stop
                    </Button>
                ) : (
                    <button
                        type="button"
                        className={styles.composerSend}
                        onClick={onSubmit}
                        disabled={!value.trim()}
                        aria-label="Send"
                    >
                        <ArrowUp size={16} weight="bold" />
                    </button>
                )}
            </div>
        </div>
    );
}

function TurnBlock({
    turn,
    editingReport,
    onSaved,
    onSubmitToolResponse,
    onStop,
}: {
    turn: Turn;
    editingReport: TurnSavedReport | null;
    onSaved: (turnId: string, savedReport: TurnSavedReport) => void;
    onSubmitToolResponse: (args: SubmitToolResponseArgs) => Promise<void>;
    onStop: () => void;
}) {
    const { assistant, tools } = turn;
    const { lead, rest } = splitLead(assistant?.text ?? '');
    // The agent is still working until its answer starts or the stream ends.
    const working = !!assistant && assistant.isStreaming && !lead;
    const hasTrail = !!assistant && !working && (assistant.steps.length > 0 || assistant.narration.length > 0);
    const showCopy = !!assistant && !!assistant.text && !assistant.isStreaming;

    return (
        <ReportTurnProvider
            initial={turn.user?.savedReport ?? null}
            editingReport={editingReport}
            onSaved={(savedReport) => onSaved(turn.id, savedReport)}
        >
            {turn.user && (
                <div className={styles.userMessage}>{turn.user.text}</div>
            )}
            {(assistant || tools.length > 0) && (
                <div className={styles.assistantBlock}>
                    {assistant && working && (
                        <>
                            {/* Only the latest line while it works; the trail keeps them all. */}
                            {assistant.narration.length > 0 && (
                                <div className={styles.narration}>
                                    {assistant.narration[assistant.narration.length - 1]}
                                </div>
                            )}
                            <StatusLine message={assistant} onStop={onStop} />
                        </>
                    )}
                    {assistant && hasTrail && <Trail message={assistant} />}
                    {lead && (
                        <div className={`${styles.assistantMessage} ${styles.assistantLead}`}>
                            <ReactMarkdown>{lead}</ReactMarkdown>
                        </div>
                    )}
                    {tools.map((message, index) => {
                        const next = tools[index + 1];
                        const previous = tools[index - 1];
                        // The download links and "Save report" arrive as two
                        // cards; they read as one line of actions.
                        if (
                            message.toolCall.name === 'showDownloadArtifact' &&
                            next?.toolCall.name === 'offerSaveReport'
                        ) {
                            return (
                                <div key={message.id} className={styles.actionRow}>
                                    <ToolCallRenderer toolCall={message.toolCall} toolCallId={message.id} />
                                    <span className={styles.actionSeparator}>·</span>
                                    <ToolCallRenderer toolCall={next.toolCall} toolCallId={next.id} />
                                </div>
                            );
                        }
                        if (
                            message.toolCall.name === 'offerSaveReport' &&
                            previous?.toolCall.name === 'showDownloadArtifact'
                        ) {
                            return null;
                        }
                        return (
                            <ToolCallRenderer
                                key={message.id}
                                toolCall={message.toolCall}
                                toolCallId={message.id}
                                onSubmitToolResponse={onSubmitToolResponse}
                            />
                        );
                    })}
                    {rest && (
                        /* Prose renders through react-markdown so bold,
                           bullets and inline code read natively. The
                           backend strips download links before they get
                           here. */
                        <div className={styles.assistantMessage}>
                            <ReactMarkdown>{rest}</ReactMarkdown>
                        </div>
                    )}
                    {assistant?.error && (
                        <div className={styles.turnError}>{assistant.error}</div>
                    )}
                    {assistant?.stopped && (
                        <div className={styles.turnStopped}>Stopped.</div>
                    )}
                    {showCopy && assistant && (
                        <AssistantCopyButton text={assistant.text} />
                    )}
                </div>
            )}
        </ReportTurnProvider>
    );
}

type AssistantTurn = Extract<ReportMessage, { role: 'assistant' }>;

/** Ticks once a second while mounted; the status line's elapsed counter. */
function useElapsedSeconds(since: number): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    return Math.max(0, Math.round((now - since) / 1000));
}

/**
 * One line that says what the agent is doing right now, with how long it
 * has been at it and a way to stop. It replaces a growing list of steps:
 * the person wants to know the work is moving, not the mechanism.
 */
function StatusLine({ message, onStop }: { message: AssistantTurn; onStop: () => void }) {
    const seconds = useElapsedSeconds(message.startedAt);
    const running = [...message.steps].reverse().find((step) => !step.done);
    const found = message.steps.filter((step) => step.done);
    return (
        <div className={styles.status}>
            <div className={styles.statusLine}>
                <span className={styles.statusPulse} aria-hidden="true" />
                <span className={styles.statusText}>
                    {running?.label ?? 'Thinking…'}
                </span>
                <span className={styles.statusTime}>{seconds} s</span>
                <button
                    type="button"
                    className={styles.statusStop}
                    onClick={onStop}
                >
                    Stop
                </button>
            </div>
            {found.length > 0 && (
                <div className={styles.findings}>
                    {found.map((step) => (
                        <div key={step.id}>{step.label}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Where the answer came from, folded to one line once it has arrived.
 * Open it to see each thing the agent read and what it said on the way.
 */
function Trail({ message }: { message: AssistantTurn }) {
    const [open, setOpen] = useState(false);
    const read = message.steps.filter((step) => step.done).length;
    const seconds = Math.round(((message.endedAt ?? Date.now()) - message.startedAt) / 1000);
    const summary = [
        read > 0 ? `Read ${read} ${read === 1 ? 'source' : 'sources'}` : null,
        `${message.steps.length} ${message.steps.length === 1 ? 'step' : 'steps'}`,
        `${seconds} s`,
    ]
        .filter(Boolean)
        .join(' · ');
    return (
        <div>
            <UnstyledButton
                className={styles.trail}
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
            >
                <CaretRight size={12} className={open ? styles.trailChevronOpen : styles.trailChevron} />
                <span>{summary}</span>
            </UnstyledButton>
            <Collapse in={open}>
                <div className={styles.trailBody}>
                    {message.narration.map((line, index) => (
                        <div key={`n-${index}`} className={styles.trailNarration}>
                            {line}
                        </div>
                    ))}
                    {message.steps.map((step) => (
                        <div key={step.id}>{step.label.replace(/…$/, '')}</div>
                    ))}
                </div>
            </Collapse>
        </div>
    );
}

/**
 * Single-line Copy button rendered under finished assistant prose.
 * Pure-prose turns (free-text path) emit no artifact card, so this is
 * how the user lifts the answer out of the chat. Also shown under
 * SUMMARIZE prose on report turns — copying the insight bullets is
 * useful when sharing them in Slack / email.
 */
function AssistantCopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    const onClick = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // navigator.clipboard is blocked on insecure contexts (older
            // tunnels, http previews). Silently no-op — the user can
            // still select the text manually.
        }
    };
    return (
        <button
            type="button"
            className={styles.assistantCopyButton}
            onClick={onClick}
            aria-label={copied ? 'Copied' : 'Copy reply'}
        >
            {copied ? (
                <>
                    <Check size={12} weight="bold" />
                    Copied
                </>
            ) : (
                <>
                    <Copy size={12} weight="regular" />
                    Copy
                </>
            )}
        </button>
    );
}
